/**
 * PlotDataPanelContainer - Container for PlotDataPanel
 *
 * Note: PlotDataPanel already receives `plots` as a prop from PlotsPanel.
 * This container extracts the remaining store access (actions, modal, panelState)
 * and passes a complete panelConfig to the presenter.
 *
 * @see docs/adr/0034-container-presenter-component-pattern.md
 */

import React from 'react';
import { Checkbox, Space, Tag } from 'antd';
import { SisternodeOutlined } from '@ant-design/icons';
import type { Plot } from '../../../../types/types';
import type { ConfigurableDataPanelConfig } from '../../../../components/ConfigurableDataPanel/ConfigurableDataPanel';
import { useEditModal } from '../../../../hooks/useModal';
import { useActions } from '../../../../hooks/useStore';
import { usePanelState } from '../../../../hooks/usePanelState';
import { useStableCallback, useRenderLoopDetection } from '../../../../hooks/hookUtils';
import styles from './PlotsPanel.module.css';
import PlotDataPanel from './PlotDataPanel';

interface PlotDataPanelContainerProps {
  plots: Plot[];
}

export function PlotDataPanelContainer({ plots }: PlotDataPanelContainerProps): React.ReactElement {
  useRenderLoopDetection('PlotDataPanelContainer');

  const actions = useActions();
  const { showEditModal } = useEditModal();
  const { filters, sort, setPanelState } = usePanelState('plotsPanel');

  /**
   * Handle plot updates with optimistic UI update.
   * Uses useStableCallback to maintain a stable reference while always
   * accessing the latest actions object.
   */
  const handleUpdate = useStableCallback((plot: Plot, updates: Partial<Plot>) => {
    actions.updatePlot({ ...plot, ...updates });
  });

  /**
   * Open the edit modal for a plot.
   * Uses useStableCallback for stable reference in ConfigurableDataPanel callbacks.
   */
  const handleEdit = useStableCallback((item: Plot | null) => {
    showEditModal({ type: 'plot', item: item });
  });

  const panelConfig: ConfigurableDataPanelConfig<Plot> = {
    title: <><SisternodeOutlined style={{ marginRight: 8 }} />Main Plots</>,
    items: plots,
    searchPlaceholder: "Search plots...",
    onAddItem: () => handleEdit(null),
    onEditItem: (item: Plot) => handleEdit(item),
    onDeleteItem: (item: Plot) => actions.deletePlot(item.id),
    itemKey: 'id',
    displayNameKey: 'plotName',
    searchKeys: ['plotName'],
    initialFilters: filters,
    initialSort: sort,
    onStateChange: setPanelState,
    filterControls: [
      { key: 'completed', placeholder: 'Filter by status', options: [{ label: 'Completed', value: true }, { label: 'In Progress', value: false }], filterFn: (item, value) => item.completed === value },
    ],
    sortOptions: [
      { label: 'Sort Order', value: (a: Plot, b: Plot) => (a?.sortOrder ?? 0) - (b?.sortOrder ?? 0) },
      { label: 'Name (A-Z)', value: (a: Plot, b: Plot) => (a?.plotName ?? '').localeCompare(b?.plotName ?? '') },
    ],
    renderItem: (plot: Plot) => (
      <div className={`${styles.plotItem} ${plot.completed ? styles.completed : ''}`} onClick={() => handleEdit(plot)}>
        <Checkbox checked={plot.completed} onChange={(e) => { e.stopPropagation(); handleUpdate(plot, { completed: !plot.completed }); }} />
        <span className={styles.plotText}>{plot.plotName}</span>
        <Space><Tag>{plot.clues?.filter(c => c.isFound).length || 0} / {plot.clues?.length || 0} clues</Tag><Tag>{plot.progressPoints?.filter(p => p.isAchieved).length || 0} / {plot.progressPoints?.length || 0} points</Tag></Space>
      </div>
    ),
  };

  return <PlotDataPanel panelConfig={panelConfig} />;
}

export default PlotDataPanelContainer;
